'use client';

import type { ReactNode } from 'react';
import { useBuyContext } from './state/BuyContext';
import { ProductCarousel, type ProductCarouselItem } from './atoms/ProductCarousel';
import { ProductHeading } from './atoms/ProductHeading';
import { BenefitList } from './atoms/BenefitList';
import { DataDrivenCards } from './DataDrivenCards';
import { CartSummaryCard } from './atoms/cart/CartSummaryCard';
import styles from './BuySection.module.css';

export type BuySectionProps = {
  carouselItems: ProductCarouselItem[];
  title: ReactNode;
  tagline?: ReactNode;
  priceStriked?: number;
  benefits?: ReactNode[];
  benefitsTitle?: ReactNode;
  showSummary?: boolean;
  summaryProps?: Parameters<typeof CartSummaryCard>[0];
  extraAfterCards?: ReactNode;
};

export const BuySection = ({
  carouselItems,
  title,
  tagline,
  priceStriked,
  benefits,
  benefitsTitle,
  showSummary = true,
  summaryProps,
  extraAfterCards,
}: BuySectionProps) => {
  const {
    basePrice,
    currency,
    installmentConfig,
    splitPaymentsDisplay,
    dispatchDate,
    onSeeAllPaymentOptions,
  } = useBuyContext();

  const heading = (
    <ProductHeading
      title={title}
      tagline={tagline}
      price={basePrice}
      strikedPrice={priceStriked}
      currency={currency}
      installmentConfig={installmentConfig}
      splitPaymentsDisplay={splitPaymentsDisplay ?? null}
      dispatchDate={dispatchDate}
      onSeeAllPaymentOptions={onSeeAllPaymentOptions}
    />
  );

  return (
    <div className={styles.container}>
      <div className={styles.mobileHeading}>{heading}</div>
      <div className={styles.carouselWrapper}>
        <ProductCarousel
          items={carouselItems}
          sticky
          navigationType="buttons"
          buttonVariant="glass"
        />
      </div>
      <div className={styles.rightColumn}>
        <div className={styles.desktopHeading}>{heading}</div>
        {benefits?.length ? (
          <BenefitList title={benefitsTitle} items={benefits} />
        ) : null}
        <DataDrivenCards />
        {extraAfterCards}
        {showSummary ? <CartSummaryCard {...(summaryProps ?? {})} /> : null}
      </div>
    </div>
  );
};
